import { EntityType } from '@converge/graphql';
import { SxProps } from '@mui/material';
import moment from 'moment-timezone';
import React, { ReactNode } from 'react';

export const getLabelFromEnum = (type: string) =>
  type
    .toLowerCase()
    .split('_')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');

const formatDateInOriginalTimezone = (dateString: string) => {
  if (!dateString) return null;

  const date = moment
    .parseZone(dateString)
    .startOf('day')
    .set({ hour: 0, minute: 1, second: 0, millisecond: 0 });
  return date.format('YYYY-MM-DDTHH:mm:ss.SSS');
};

export const formatValue = (value: Date | null) => {
  return value ? formatDateInOriginalTimezone(value.toString()) : null;
};

type DateMode = 'past' | 'future';

export const validateDateBase = (
  date: Date | null,
  mode: DateMode,
  inputValue?: string
): { isInvalid: boolean; errorMessage: string } => {
  let isInvalid = false;
  let errorMessage = '';
  const dateFormatError = 'Date format must be MM/DD/YYYY';

  // Check if we have a date object or a date string
  const hasDateValue = date || (inputValue && inputValue.trim());

  if (hasDateValue) {
    // Get today in local timezone as YYYY-MM-DD string for accurate comparison
    const today = new Date();
    const todayString =
      today.getFullYear() +
      '-' +
      String(today.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(today.getDate()).padStart(2, '0');

    let inputDateString = '';

    if (date) {
      // If we have a date object, check if it's valid first
      isInvalid = formatValue(date) === 'Invalid date';

      if (isInvalid) {
        errorMessage = dateFormatError;
        return { isInvalid, errorMessage };
      }

      // Convert date object to YYYY-MM-DD string
      const inputDate = new Date(date);
      inputDateString =
        inputDate.getFullYear() +
        '-' +
        String(inputDate.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(inputDate.getDate()).padStart(2, '0');
    } else if (inputValue && inputValue.trim()) {
      // If we have a string in YYYY-MM-DD format, use it directly
      const dateValue = inputValue.trim();

      // Check if it's in YYYY-MM-DD format
      const yyyymmddRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
      if (yyyymmddRegex.test(dateValue)) {
        inputDateString = dateValue;
      } else {
        // Check MM/DD/YYYY format
        const mmddyyyyRegex =
          /^(0?[1-9]|1[012])\/(0?[1-9]|[12][0-9]|3[01])\/(19|20)\d\d$/;
        if (!mmddyyyyRegex.test(dateValue)) {
          isInvalid = true;
          errorMessage = dateFormatError;
          return { isInvalid, errorMessage };
        }
        // Convert MM/DD/YYYY to YYYY-MM-DD for comparison
        const [month, day, year] = dateValue.split('/');
        inputDateString = `${year}-${month.padStart(2, '0')}-${day.padStart(
          2,
          '0'
        )}`;
      }
    }

    // Now do the date comparison
    if (mode === 'past' && inputDateString > todayString) {
      isInvalid = true;
      errorMessage = 'Date must be today or in the past';
    }

    if (mode === 'future' && inputDateString < todayString) {
      isInvalid = true;
      errorMessage = 'Date must be today or in the future';
    }
  }

  return { isInvalid, errorMessage };
};
export type ValidateDateType = (
  date: Date | null,
  inputValue?: string
) => {
  isInvalid: boolean;
  errorMessage: string;
};
export const validatePastDate = (date: Date | null, inputValue?: string) => {
  return validateDateBase(date, 'past', inputValue);
};
export const validateFutureDate = (date: Date | null, inputValue?: string) => {
  return validateDateBase(date, 'future', inputValue);
};

export const validateSetErrors = <T extends string>(
  name: T,
  date: Date | null,
  inputValue: string | undefined,
  formMethods: {
    setError: (name: T, error: { type: string; message: string }) => void;
    clearErrors: (name: T) => void;
  }
) => {
  const { isInvalid, errorMessage } = validatePastDate(
    date ? new Date(date) : null,
    inputValue
  );

  // Use form context to set/clear errors
  if (isInvalid) {
    formMethods.setError(name, {
      type: 'validation',
      message: errorMessage,
    });
  } else {
    formMethods.clearErrors(name);
  }

  return { isInvalid, errorMessage };
};

export const getEntityTypeLabel = (entityType?: EntityType | null): string => {
  switch (entityType) {
    case EntityType.All:
      return 'All';
    case EntityType.Corporation:
      return 'Corporation';
    case EntityType.NonProfit:
      return 'Non-Profit';
    case EntityType.Partnership:
      return 'Partnership';
    case EntityType.Private:
      return 'Private';
    case EntityType.Proprietorship:
      return 'Proprietorship';
    case EntityType.Subsidiary:
      return 'Subsidiary';
    default:
      return 'Corporation';
  }
};

type ModalCommonStyleProps = {
  height?: number | string;
  width?: number;
  top?: string;
};
export const modalCommonStyle = ({
  height = 400,
  width = 800,
  top = '50%',
}: ModalCommonStyleProps): SxProps => {
  return {
    position: 'absolute' as const,
    top,
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    height,
    overflowY: 'auto',
  };
};

export const highlightTextForFilter = (
  text: string,
  term: string | null | undefined
): ReactNode => {
  if (!term || !text) return text;
  const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escapedTerm})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === term.toLowerCase() ? (
          <span key={i} style={{ backgroundColor: 'rgb(181, 251, 241)' }}>
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
};
